import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useDataCache } from '../context/DataCacheContext';
import axios from 'axios';
import { cachedGet } from '../utils/apiCache';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import AppLayout from '../components/AppLayout';
import { 
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  Sparkles, 
  Briefcase, 
  Search,
  MapPin,
  Building2,
  Clock,
  ExternalLink,
  Zap,
  MousePointer,
  Loader2,
  DollarSign,
  Filter,
  RefreshCw,
  Wand2,
  Globe,
  Wifi,
  Check,
  Save,
  Target,
  Award,
  FileText,
  ChevronDown,
  ChevronUp,
  Download
} from 'lucide-react';

const SHOW_BURST_AUTO_APPLY = false;
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Strip HTML tags and decode entities for clean text display
const stripHtml = (html) => {
  if (!html) return '';
  return html
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/p>/gi, '\n')
    .replace(/<\/li>/gi, '\n')
    .replace(/<\/h[1-6]>/gi, '\n')
    .replace(/<[^>]*>/g, '')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&nbsp;/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
};

const LOGO_COLORS = [
  'bg-rose-500', 'bg-orange-500', 'bg-amber-500', 'bg-emerald-500',
  'bg-teal-500', 'bg-cyan-500', 'bg-blue-500', 'bg-indigo-500',
  'bg-violet-500', 'bg-pink-500', 'bg-red-500', 'bg-sky-500',
];

const CompanyLogo = ({ company, size = 'md' }) => {
  const [imgError, setImgError] = useState(false);
  const name = company || 'Company';
  const initial = name.charAt(0).toUpperCase();
  
  // Generate a consistent color from company name
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  const colorClass = LOGO_COLORS[hash % LOGO_COLORS.length];
  
  // Try Clearbit logo (free, no key needed)
  const domain = name.toLowerCase().replace(/[^a-z0-9]/g, '') + '.com';
  const logoUrl = `https://logo.clearbit.com/${domain}`;
  
  const sizeClasses = size === 'sm' ? 'w-8 h-8 text-xs' : 'w-10 h-10 sm:w-12 sm:h-12 text-sm sm:text-base';
  
  if (imgError) {
    return (
      <div className={`${sizeClasses} ${colorClass} rounded-xl flex items-center justify-center shrink-0`}>
        <span className="font-bold text-white">{initial}</span>
      </div>
    );
  }
  
  return (
    <div className={`${sizeClasses} rounded-xl overflow-hidden shrink-0 bg-white dark:bg-zinc-800 flex items-center justify-center`}>
      <img
        src={logoUrl}
        alt={name}
        className="w-full h-full object-contain p-1.5"
        onError={() => setImgError(true)}
        loading="lazy"
      />
    </div>
  );
};


const JobCard = ({ job, onApply, onOptimize, onViewDetail, onDismiss, cvs, batchMode, batchSelected, onBatchToggle, usageData }) => {
  const sourceColors = {
    linkedin: 'bg-blue-500',
    indeed: 'bg-purple-500',
    glassdoor: 'bg-green-500',
    google: 'bg-red-500',
    ziprecruiter: 'bg-emerald-500',
    adzuna: 'bg-orange-500',
    remotive: 'bg-cyan-500',
    jobicy: 'bg-teal-500',
    themuse: 'bg-pink-500',
    himalayas: 'bg-sky-500',
    remoteok: 'bg-yellow-500',
    arbeitnow: 'bg-amber-500',
    dice: 'bg-rose-500',
    monster: 'bg-violet-500',
    jsearch: 'bg-indigo-500',
  };

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-200, 200], [-4, 4]);
  const applyOpacity = useTransform(x, [0, 80], [0, 1]);
  const skipOpacity = useTransform(x, [-80, 0], [1, 0]);
  const [swiped, setSwiped] = useState(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDragEnd = (_, info) => {
    const swipe = info.offset.x;
    const velocity = info.velocity.x;
    if (swipe > 80 || velocity > 400) {
      setSwiped('right');
      setTimeout(() => onApply(job), 300);
    } else if (swipe < -80 || velocity < -400) {
      setSwiped('left');
      setTimeout(() => onDismiss(job.id), 300);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const matchColor = job.no_cv ? 'bg-indigo-50 text-indigo-600 border-indigo-200'
    : job.match_score >= 80 ? 'bg-green-100 text-green-700 border-green-200'
    : job.match_score >= 60 ? 'bg-teal-100 text-teal-700 border-teal-200'
    : job.match_score >= 40 ? 'bg-yellow-100 text-yellow-700 border-yellow-200'
    : 'bg-zinc-100 text-zinc-600 border-zinc-200';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={swiped ? { x: swiped === 'right' ? 400 : -400, opacity: 0, transition: { duration: 0.25 } } : { opacity: 1, y: 0 }}
      style={isMobile && !batchMode ? { x, rotate, touchAction: 'pan-y' } : {}}
      drag={isMobile && !batchMode ? 'x' : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.5}
      dragTransition={{ bounceStiffness: 300, bounceDamping: 25 }}
      onDragEnd={handleDragEnd}
      className={`bg-white dark:bg-[#1c1c1c] rounded-2xl border ${batchMode && batchSelected ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-zinc-100 dark:border-white/[0.08]'} p-4 sm:p-6 shadow-soft hover:shadow-hover sm:hover:-translate-y-1 transition-shadow duration-300 overflow-hidden cursor-pointer relative`}
      data-testid={`job-card-${job.id}`}
      onClick={batchMode ? () => onBatchToggle(job) : () => onViewDetail(job)}
    >
      {/* Mobile swipe indicators */}
      {isMobile && !batchMode && (
        <>
          <motion.div
            className="absolute top-3 right-3 z-10 bg-emerald-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg"
            style={{ opacity: applyOpacity }}
            data-testid="swipe-apply-indicator"
          >
            APPLY
          </motion.div>
          <motion.div
            className="absolute top-3 left-3 z-10 bg-rose-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg"
            style={{ opacity: skipOpacity }}
            data-testid="swipe-skip-indicator"
          >
            SKIP
          </motion.div>
        </>
      )}
      {batchMode && (
        <div className="flex items-center gap-2 mb-3 pb-3 border-b border-zinc-100">
          <div className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${batchSelected ? 'bg-indigo-500 border-indigo-500' : 'border-zinc-300 bg-white'}`}>
            {batchSelected && <Check className="w-3.5 h-3.5 text-white" />}
          </div>
          <span className="text-xs text-zinc-500">{batchSelected ? 'Selected for batch' : 'Click to select'}</span>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-2 mb-3 sm:mb-4">
        <div className="flex items-center gap-3">
          <CompanyLogo company={job.company} />
          <div className="min-w-0">
            <h3 className="font-heading font-semibold text-base sm:text-lg text-zinc-900 dark:text-white line-clamp-2 break-words">{job.job_title}</h3>
            <p className="text-sm text-zinc-600 dark:text-zinc-400">{job.company}</p>
          </div>
        </div>
        <div className="flex items-center sm:flex-col sm:items-end gap-2 ml-13 sm:ml-0">
          {job.no_cv ? (
            <Link to="/cv" className={`px-3 py-1 rounded-full text-xs font-bold border ${matchColor} hover:bg-indigo-100 transition-colors`} data-testid={`upload-cv-prompt-${job.id}`}>
              Upload CV to match
            </Link>
          ) : job.match_score != null && (
            <span className={`px-3 py-1 rounded-full text-xs font-bold border ${matchColor}`} data-testid={`match-score-${job.id}`}>
              {job.match_score}% Match
            </span>
          )}
          <span className={`px-3 py-1 rounded-full text-xs font-medium text-white capitalize ${sourceColors[job.source] || 'bg-zinc-500'}`}>
            {job.source}
          </span>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 sm:gap-3 mb-3 text-xs sm:text-sm text-zinc-500 dark:text-zinc-400">
        <div className="flex items-center gap-1">
          <MapPin className="w-4 h-4" />
          <span className="line-clamp-1">{job.location}</span>
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" />
          <span>{formatDate(job.posted_at)}</span>
        </div>
        {job.salary_range && (
          <div className="flex items-center gap-1 text-green-600 font-medium">
            <DollarSign className="w-4 h-4" />
            <span>{job.salary_range}</span>
          </div>
        )}
        {job.is_remote && (
          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-teal-100 text-teal-700">Remote</span>
        )}
      </div>

      <p className="text-zinc-600 dark:text-zinc-400 text-sm mb-3 line-clamp-2">{stripHtml(job.description)}</p>

      {/* Matched / Missing Skills */}
      {(job.matched_skills?.length > 0 || job.missing_skills?.length > 0) && (
        <div className="mb-4 space-y-2">
          {job.matched_skills?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {job.matched_skills.slice(0, 4).map((skill, i) => (
                <span key={i} className="px-2 py-0.5 bg-green-50 text-green-700 border border-green-200 rounded-full text-xs capitalize" data-testid={`matched-skill-${i}`}>
                  {skill}
                </span>
              ))}
            </div>
          )}
          {job.missing_skills?.length > 0 && (
            <div className="flex flex-wrap gap-1.5">
              {job.missing_skills.slice(0, 3).map((skill, i) => (
                <span key={i} className="px-2 py-0.5 bg-red-50 text-red-500 border border-red-200 rounded-full text-xs capitalize" data-testid={`missing-skill-${i}`}>
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Requirements fallback if no skill data */}
      {!job.matched_skills?.length && !job.missing_skills?.length && (
        <div className="flex flex-wrap gap-2 mb-4">
          {(job.requirements || []).slice(0, 3).map((req, i) => (
            <span key={i} className="px-3 py-1 bg-zinc-100 rounded-full text-xs text-zinc-600">{req}</span>
          ))}
        </div>
      )}

      {/* Usage warning */}
      {usageData && usageData.usage_percentage >= 75 && usageData.plan !== 'unlimited' && (
        <div className={`flex items-center gap-2 mb-3 px-3 py-2 rounded-lg text-xs font-medium ${
          usageData.usage_percentage >= 90 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
        }`} data-testid="job-card-usage-warning">
          <Zap className="w-3.5 h-3.5 flex-shrink-0" />
          <span>{usageData.usage_label}</span>
          <span className="text-[10px] opacity-70">—</span>
          <Link to="/subscription" className="underline hover:no-underline">{usageData.usage_percentage >= 90 ? 'Upgrade?' : 'View plans'}</Link>
        </div>
      )}

      <div className="flex items-center gap-2" onClick={(e) => e.stopPropagation()}>
        <Button
          onClick={() => onApply(job)}
          className="flex-1 min-w-0 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-xs sm:text-sm"
          data-testid={`apply-btn-${job.id}`}
        >
          {job.source === 'linkedin' ? (
            <><Wand2 className="w-4 h-4 mr-1 sm:mr-2 shrink-0" /><span className="truncate">Tailor & Apply</span></>
          ) : (
            <><Wand2 className="w-4 h-4 mr-1 sm:mr-2 shrink-0" /><span className="truncate">Tailor & Apply</span></>
          )}
        </Button>
        <Button
          variant="outline"
          onClick={() => onOptimize(job)}
          className="rounded-xl border-zinc-200 dark:border-white/[0.1] shrink-0 px-3"
          title="Optimize CV for this job"
          data-testid={`optimize-btn-${job.id}`}
        >
          <Wand2 className="w-4 h-4" />
        </Button>
        <Button
          variant="outline"
          onClick={() => window.open(job.url, '_blank')}
          className="rounded-xl border-zinc-200 dark:border-white/[0.1] shrink-0 px-3"
          data-testid={`view-job-btn-${job.id}`}
        >
          <ExternalLink className="w-4 h-4" />
        </Button>
      </div>
    </motion.div>
  );
};

const JobFeed = () => {
  const [jobs, setJobs] = useState([]);
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [locationFilter, setLocationFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('all');
  const [remoteOnly, setRemoteOnly] = useState(false);
  const [regionFilter, setRegionFilter] = useState('all');
  const [salaryRange, setSalaryRange] = useState([0, 300000]);
  const [companyFilter, setCompanyFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [postedWithin, setPostedWithin] = useState('all');
  const [categoryStats, setCategoryStats] = useState(null);
  const [showFilters, setShowFilters] = useState(false);
  const [applyDialog, setApplyDialog] = useState({ open: false, job: null });
  const [optimizeDialog, setOptimizeDialog] = useState({ open: false, job: null, result: null });
  const [expandedVersion, setExpandedVersion] = useState(null);
  const [compareVersions, setCompareVersions] = useState([]);
  const [showCompare, setShowCompare] = useState(false);
  const [selectedCv, setSelectedCv] = useState('');
  const [generating, setGenerating] = useState(false);
  const [coverLetter, setCoverLetter] = useState('');
  const [selectedTone, setSelectedTone] = useState('professional');
  const [regionStats, setRegionStats] = useState(null);
  const [batchSelectMode, setBatchSelectMode] = useState(false);
  const [batchSelectedJobs, setBatchSelectedJobs] = useState([]);
  const [batchDialog, setBatchDialog] = useState({ open: false, result: null, expandedCell: null });
  const [bulkApplying, setBulkApplying] = useState(false);
  const [bulkApplyProgress, setBulkApplyProgress] = useState({ done: 0, total: 0, active: false });
  const [usageData, setUsageData] = useState(null);
  const [jobDetail, setJobDetail] = useState({ open: false, job: null });
  const [fullDesc, setFullDesc] = useState({ loading: false, text: null, source: null });
  const [confirmationDialog, setConfirmationDialog] = useState({ open: false, data: null });
  const [dismissedJobs, setDismissedJobs] = useState(new Set());
  const [burstApplying, setBurstApplying] = useState(null);  // null | 50 | 100 | 200 | 500
  const { getAuthHeaders, user } = useAuth();
  const dataCache = useDataCache();
  const navigate = useNavigate();
  const abortRef = useRef(null);
  const MAX_JOBS_IN_MEMORY = 200;

  useEffect(() => {
    // Restore cached jobs instantly
    const cached = dataCache?.get('jobfeed');
    if (cached) {
      setJobs(cached.jobs);
      setCvs(cached.cvs);
      if (cached.cvs.length > 0) setSelectedCv(cached.cvs[0].id);
      setLoading(false);
    }
    fetchData();
    fetchRegionStats();
    fetchCategoryStats();
    fetchUsageData();
    return () => { if (abortRef.current) abortRef.current.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch when region, remote, category, location, source, time, or company filter changes (server-side filtering)
  useEffect(() => {
    fetchJobs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regionFilter, remoteOnly, categoryFilter, postedWithin, sourceFilter]);

  // Debounce location filter and search query - re-fetch after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchJobs();
    }, 500);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationFilter, searchQuery]);

  const fetchRegionStats = async () => {
    try {
      const { data } = await cachedGet(`${API}/jobs/stats/regions`, { headers: getAuthHeaders() }, 5 * 60 * 1000, setRegionStats);
      setRegionStats(data);
    } catch (error) {
      console.error('Failed to fetch region stats:', error);
    }
  };

  const fetchCategoryStats = async () => {
    try {
      const { data } = await cachedGet(`${API}/jobs/stats/categories`, { headers: getAuthHeaders() }, 5 * 60 * 1000, setCategoryStats);
      setCategoryStats(data);
    } catch (error) {
      console.error('Failed to fetch category stats:', error);
    }
  };

  const fetchUsageData = async () => {
    try {
      const { data } = await cachedGet(`${API}/subscription/current`, { headers: getAuthHeaders() }, 60 * 1000, setUsageData);
      setUsageData(data);
    } catch (error) { console.error("Usage data fetch:", error); }
  };

  const buildQueryParams = (skip = 0) => {
    const params = new URLSearchParams({ limit: '50', skip: String(skip) });
    if (regionFilter === 'remote' || remoteOnly) params.append('remote', 'true');
    if (regionFilter !== 'all' && regionFilter !== 'remote') params.append('region', regionFilter);
    if (categoryFilter !== 'all') params.append('category', categoryFilter);
    if (locationFilter.trim()) params.append('location', locationFilter.trim());
    if (postedWithin !== 'all') params.append('posted_within', postedWithin);
    if (sourceFilter && sourceFilter !== 'all') params.append('source', sourceFilter);
    if (companyFilter && companyFilter.trim()) params.append('company', companyFilter.trim());
    if (salaryRange && salaryRange[0] > 0) params.append('salary_min', String(salaryRange[0]));
    if (searchQuery && searchQuery.trim()) params.append('query', searchQuery.trim());
    return params.toString();
  };

  const fetchJobs = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/jobs/feed-scored?${buildQueryParams(0)}`, { 
        headers: getAuthHeaders(),
        signal: controller.signal
      });
      if (!controller.signal.aborted) {
        setJobs(res.data.slice(0, MAX_JOBS_IN_MEMORY));
        setHasMore(res.data.length >= 50);
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Failed to fetch jobs:', error);
        toast.error('Failed to load jobs');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [getAuthHeaders, regionFilter, remoteOnly, categoryFilter, locationFilter, postedWithin]);

  const fetchData = useCallback(async () => {
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      const [jobsRes, cvsRes] = await Promise.all([
        axios.get(`${API}/jobs/feed-scored?${buildQueryParams(0)}`, { 
          headers: getAuthHeaders(),
          signal: controller.signal
        }),
        axios.get(`${API}/cv/list`, { 
          headers: getAuthHeaders(),
          signal: controller.signal
        })
      ]);
      if (!controller.signal.aborted) {
        setJobs(jobsRes.data.slice(0, MAX_JOBS_IN_MEMORY));
        setHasMore(jobsRes.data.length >= 50);
        setCvs(cvsRes.data);
        if (cvsRes.data.length > 0) setSelectedCv(cvsRes.data[0].id);
        dataCache?.set('jobfeed', { jobs: jobsRes.data.slice(0, MAX_JOBS_IN_MEMORY), cvs: cvsRes.data });
      }
    } catch (error) {
      if (error.name !== 'CanceledError' && error.code !== 'ERR_CANCELED') {
        console.error('Failed to fetch jobs:', error);
        toast.error('Failed to load jobs');
      }
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [getAuthHeaders]);

  const loadMore = async () => {
    if (jobs.length >= MAX_JOBS_IN_MEMORY) {
      setHasMore(false);
      return;
    }
    setLoadingMore(true);
    try {
      const res = await axios.get(`${API}/jobs/feed-scored?${buildQueryParams(jobs.length)}`, { headers: getAuthHeaders() });
      if (res.data.length > 0) {
        setJobs(prev => {
          const combined = [...prev, ...res.data];
          return combined.slice(0, MAX_JOBS_IN_MEMORY);
        });
        setHasMore(res.data.length >= 50 && jobs.length + res.data.length < MAX_JOBS_IN_MEMORY);
      } else {
        setHasMore(false);
      }
    } catch (error) {
      if (error.name !== 'CanceledError') toast.error('Failed to load more jobs');
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRegionChange = (region) => {
    setRegionFilter(region);
    if (region === 'remote') setRemoteOnly(true);
    else setRemoteOnly(false);
  };

  const refreshJobs = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/jobs/fetch`, null, { headers: getAuthHeaders() });
      await fetchData();
      toast.success('Jobs refreshed!');
    } catch (error) {
      if (error.name !== 'CanceledError') toast.error('Failed to refresh jobs');
    } finally {
      setRefreshing(false);
    }
  };

  const filteredJobs = useMemo(() => {
    const sq = searchQuery.toLowerCase();
    const sf = sourceFilter.toLowerCase();
    const cf = companyFilter.toLowerCase();
    return jobs.filter(job => {
      if (dismissedJobs.has(job.id)) return false;
      if (sq && !(
        job.job_title?.toLowerCase().includes(sq) ||
        job.company?.toLowerCase().includes(sq) ||
        job.location?.toLowerCase().includes(sq)
      )) return false;
      if (sf !== 'all' && job.source?.toLowerCase() !== sf) return false;
      if (remoteOnly && !job.is_remote) return false;
      if (job.salary_min && (job.salary_min < salaryRange[0] || (job.salary_max || job.salary_min) > salaryRange[1])) return false;
      if (cf && !job.company?.toLowerCase().includes(cf)) return false;
      return true;
    });
  }, [jobs, searchQuery, sourceFilter, remoteOnly, salaryRange, companyFilter, dismissedJobs]);

  const handleDismissJob = (jobId) => {
    setDismissedJobs(prev => new Set([...prev, jobId]));
    toast('Job skipped', { duration: 2000 });
  };

  const handleApply = async (job) => {
    let currentCvs = cvs;
    if (currentCvs.length === 0) {
      try {
        const res = await axios.get(`${API}/cv/list`, { headers: getAuthHeaders() });
        currentCvs = res.data || [];
        if (currentCvs.length > 0) {
          setCvs(currentCvs);
          setSelectedCv(currentCvs[0].id);
        }
      } catch (e) { /* fall through to error */ }
    }
    if (currentCvs.length === 0) {
      toast.error('Please upload a CV first to apply for jobs', {
        action: { label: 'Upload CV', onClick: () => window.location.href = '/cv' }
      });
      return;
    }
    setApplyDialog({ open: true, job });
    setCoverLetter('');
    const cvToUse = selectedCv || (currentCvs.length > 0 ? currentCvs[0].id : null);
    if (cvToUse) {
      setTimeout(() => {
        generateCoverLetterForJob(job, cvToUse);
      }, 300);
    }
  };

  const handleOptimize = async (job) => {
    let currentCvs = cvs;
    if (currentCvs.length === 0) {
      try {
        const res = await axios.get(`${API}/cv/list`, { headers: getAuthHeaders() });
        currentCvs = res.data || [];
        if (currentCvs.length > 0) {
          setCvs(currentCvs);
          setSelectedCv(currentCvs[0].id);
        }
      } catch (e) { /* fall through to error */ }
    }
    if (currentCvs.length === 0) {
      toast.error('Please upload a CV first', {
        action: { label: 'Upload CV', onClick: () => window.location.href = '/cv' }
      });
      return;
    }
    setOptimizeDialog({ open: true, job, result: null, saving: null });
    setExpandedVersion(null);
    setCompareVersions([]);
    setShowCompare(false);
    
    const cvToUse = selectedCv || (cvs.length > 0 ? cvs[0].id : null);
    if (!cvToUse) {
      toast.error('No CV available');
      setOptimizeDialog({ open: false, job: null, result: null, saving: null });
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/ai/optimize-cv`,
        { cv_id: cvToUse, job_id: job.id },
        { headers: getAuthHeaders() }
      );
      
      const optId = response.data.optimization_id;
      toast.success('Generating 2 optimized CV versions...');
      
      // Poll for completion
      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await axios.get(
            `${API}/ai/optimize-cv/${optId}`,
            { headers: getAuthHeaders() }
          );
          if (pollRes.data.status === 'complete' && pollRes.data.versions?.length > 0) {
            clearInterval(pollInterval);
            setOptimizeDialog(prev => ({ ...prev, result: pollRes.data }));
          } else if (pollRes.data.status === 'failed') {
            clearInterval(pollInterval);
            toast.error('CV optimization failed. Please try again.');
            setOptimizeDialog({ open: false, job: null, result: null, saving: null });
          }
        } catch (e) {
          console.error("Polling error:", e);
        }
      }, 3000);
      
      setTimeout(() => clearInterval(pollInterval), 120000);
      
    } catch (error) {
      toast.error('Failed to optimize CV');
      setOptimizeDialog({ open: false, job: null, result: null, saving: null });
    }
  };

  const handleSaveOptimizedCV = async (optimizationId, versionId, label) => {
    setOptimizeDialog(prev => ({ ...prev, saving: versionId }));
    try {
      const response = await axios.post(
        `${API}/cv/save-optimized`,
        { optimization_id: optimizationId, version_id: versionId },
        { headers: getAuthHeaders() }
      );
      toast.success(`"${label}" saved to My CVs!`);
      setOptimizeDialog(prev => ({ ...prev, saving: null }));
    } catch (error) {
      toast.error('Failed to save CV');
      setOptimizeDialog(prev => ({ ...prev, saving: null }));
    }
  };

  const handleDownloadOptimizedPDF = async (optimizationId, versionId, label) => {
    try {
      toast.info(`Generating ${label} PDF...`);
      const response = await axios.post(
        `${API}/cv/download-optimized-pdf`,
        { optimization_id: optimizationId, version_id: versionId },
        { headers: getAuthHeaders(), responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `CV_${label.replace(/\s+/g, '_')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded!');
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  const toggleBatchJob = (job) => {
    setBatchSelectedJobs(prev => {
      const exists = prev.find(j => j.id === job.id);
      if (exists) return prev.filter(j => j.id !== job.id);
      if (prev.length >= 100) { toast.error('Maximum 100 jobs per batch'); return prev; }
      return [...prev, { id: job.id, job_title: job.job_title, company: job.company }];
    });
  };

  const handleBatchOptimize = async () => {
    if (batchSelectedJobs.length < 2) { toast.error('Select at least 2 jobs'); return; }
    const cvToUse = selectedCv || (cvs.length > 0 ? cvs[0].id : null);
    if (!cvToUse) { toast.error('Please upload a CV first'); return; }

    setBatchDialog({ open: true, result: null, expandedCell: null });
    setBatchSelectMode(false);

    try {
      const response = await axios.post(
        `${API}/ai/batch-optimize-cv`,
        { cv_id: cvToUse, job_ids: batchSelectedJobs.map(j => j.id) },
        { headers: getAuthHeaders() }
      );
      const batchId = response.data.batch_id;
      toast.success(`Optimizing CV for ${response.data.total_jobs} jobs...`);

      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await axios.get(
            `${API}/ai/batch-optimize-cv/${batchId}`,
            { headers: getAuthHeaders() }
          );
          setBatchDialog(prev => ({ ...prev, result: pollRes.data }));
          if (pollRes.data.status === 'complete' || pollRes.data.status === 'failed') {
            clearInterval(pollInterval);
          }
        } catch { /* keep polling */ }
      }, 4000);
      setTimeout(() => clearInterval(pollInterval), 300000);
    } catch (error) {
      toast.error('Failed to start batch optimization');
      setBatchDialog({ open: false, result: null, expandedCell: null });
    }
  };

  const selectAllVisible = () => {
    const toSelect = filteredJobs.slice(0, 100).map(j => ({ id: j.id, job_title: j.job_title, company: j.company }));
    setBatchSelectedJobs(toSelect);
    toast.success(`Selected ${toSelect.length} jobs`);
  };

  const handleBurstApply = async (targetCount) => {
    const plan = user?.subscription_plan;
    if (plan !== 'power' && plan !== 'unlimited' && !user?.is_founder) {
      toast.error('Burst auto-apply requires the Power or Unlimited plan');
      navigate('/subscription');
      return;
    }
    setBurstApplying(targetCount);
    try {
      const res = await axios.post(
        `${API}/auto-apply/burst`,
        { target_count: targetCount, min_match_score: 50, remote_only: false },
        { headers: getAuthHeaders() }
      );
      toast.success(res.data.message || `Auto-applying to ${targetCount} jobs in the background.`);
    } catch (err) {
      const detail = err.response?.data?.detail || 'Failed to start burst auto-apply';
      toast.error(detail);
      // If user genuinely has no CV, send them to the CV upload page
      if (detail.toLowerCase().includes('upload a cv')) {
        setTimeout(() => navigate('/cv'), 1200);
      }
    } finally {
      // Keep button "loading" for 2s for user feedback, then unlock
      setTimeout(() => setBurstApplying(null), 2000);
    }
  };

  const handleBulkApply = async () => {
    if (batchSelectedJobs.length === 0) { toast.error('Select jobs first'); return; }
    const cvToUse = selectedCv || (cvs.length > 0 ? cvs[0].id : null);
    if (!cvToUse) { toast.error('Please upload a CV first'); return; }

    setBulkApplying(true);
    setBulkApplyProgress({ done: 0, total: batchSelectedJobs.length, active: true });

    try {
      const response = await axios.post(
        `${API}/applications/bulk-apply`,
        { cv_id: cvToUse, job_ids: batchSelectedJobs.map(j => j.id), tone: selectedTone },
        { headers: getAuthHeaders() }
      );
      
      const bulkId = response.data.bulk_id;
      toast.success(`Applying to ${batchSelectedJobs.length} jobs...`);

      const pollInterval = setInterval(async () => {
        try {
          const pollRes = await axios.get(
            `${API}/applications/bulk-apply/${bulkId}`,
            { headers: getAuthHeaders() }
          );
          setBulkApplyProgress({ done: pollRes.data.completed, total: pollRes.data.total, active: pollRes.data.status === 'processing' });
          if (pollRes.data.status === 'complete' || pollRes.data.status === 'failed') {
            clearInterval(pollInterval);
            setBulkApplying(false);
            setBatchSelectMode(false);
            setBatchSelectedJobs([]);
            toast.success(`🚀 Applied to ${pollRes.data.completed} jobs!`);
            fetchUsageData();
          }
        } catch { /* keep polling */ }
      }, 2000);
      setTimeout(() => { clearInterval(pollInterval); setBulkApplying(false); }, 600000);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to bulk apply';
      toast.error(message);
      setBulkApplying(false);
      setBulkApplyProgress({ done: 0, total: 0, active: false });
    }
  };

  // Auto-generate cover letter when dialog opens (called from handleApply with job passed directly)
  const generateCoverLetterForJob = async (job, cvId, tone) => {
    if (!cvId || !job || !job.id) {
      if (!job?.id) toast.error('Unable to generate cover letter for this job. Please refresh and try another.');
      return;
    }
    const useTone = tone || selectedTone;
    setSelectedTone(useTone);
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/ai/generate-cover-letter`,
        { cv_id: cvId, job_id: job.id, tone: useTone },
        { headers: getAuthHeaders() }
      );
      setCoverLetter(response.data.cover_letter);
      toast.success(`${useTone.charAt(0).toUpperCase() + useTone.slice(1)} cover letter generated!`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate cover letter';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const generateCoverLetter = async (tone) => {
    if (!selectedCv || !applyDialog.job || !applyDialog.job.id) return;
    const useTone = tone || selectedTone;
    setSelectedTone(useTone);
    setGenerating(true);
    try {
      const response = await axios.post(
        `${API}/ai/generate-cover-letter`,
        { cv_id: selectedCv, job_id: applyDialog.job.id, tone: useTone },
        { headers: getAuthHeaders() }
      );
      setCoverLetter(response.data.cover_letter);
      toast.success(`${useTone.charAt(0).toUpperCase() + useTone.slice(1)} cover letter generated!`);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to generate cover letter';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  const submitApplication = async () => {
    if (!selectedCv || !applyDialog.job) return;
    setGenerating(true);
    try {
      const isAutoApply = false; // v1: JobSurfAI prepares the application; the user submits it on the employer's site
      const response = await axios.post(
        `${API}/applications/create`,
        { cv_id: selectedCv, job_id: applyDialog.job.id, cover_letter: coverLetter, auto_apply: isAutoApply },
        { headers: getAuthHeaders() }
      );
      const appliedJob = applyDialog.job;
      const cvUsed = cvs.find(c => c.id === selectedCv);
      setApplyDialog({ open: false, job: null });
      // Show confirmation dialog
      setConfirmationDialog({
        open: true,
        data: {
          job_title: appliedJob.job_title,
          company: appliedJob.company,
          location: appliedJob.location,
          source: appliedJob.source,
          cv_filename: cvUsed?.filename || 'Your CV',
          cover_letter_preview: coverLetter?.slice(0, 200) || '',
          applied_at: new Date().toLocaleString(),
          status: response.data.status || 'applied',
          job_url: appliedJob.url,
        }
      });
      setCoverLetter('');
      fetchUsageData();
    } catch (error) {
      const message = error.response?.data?.detail || 'Could not prepare the application';
      toast.error(message);
    } finally {
      setGenerating(false);
    }
  };

  return (
    <AppLayout>
      <div>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-4">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold text-zinc-900 dark:text-white mb-1">Job Feed</h1>
              <p className="text-sm sm:text-base text-zinc-600 dark:text-zinc-400">Browse and apply to jobs matching your profile</p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => { setBatchSelectMode(!batchSelectMode); setBatchSelectedJobs([]); setBulkApplyProgress({ done: 0, total: 0, active: false }); }}
                variant={batchSelectMode ? "default" : "outline"}
                className={`rounded-xl text-xs sm:text-sm ${batchSelectMode ? 'bg-indigo-500 hover:bg-indigo-600 text-white' : ''}`}
                data-testid="batch-select-toggle"
              >
                <Sparkles className="w-4 h-4 mr-1 sm:mr-2" />
                {batchSelectMode ? 'Cancel' : 'Batch Select'}
              </Button>
              <Button onClick={refreshJobs} disabled={refreshing} variant="outline" className="rounded-xl text-xs sm:text-sm" data-testid="refresh-jobs-btn">
                <RefreshCw className={`w-4 h-4 mr-1 sm:mr-2 ${refreshing ? 'animate-spin' : ''}`} />
                Refresh
              </Button>
            </div>
          </div>

          {/* Region Filter Tabs */}
          <div className="flex gap-2 mb-6 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide" data-testid="region-tabs">
            {[
              { id: 'all', label: 'All Jobs', icon: <Globe className="w-4 h-4" />, count: regionStats?.total },
              { id: 'remote', label: 'Remote', icon: <Wifi className="w-4 h-4" />, count: regionStats?.remote },
              { id: 'europe', label: 'Europe', icon: null, emoji: 'EU', count: regionStats?.europe },
              { id: 'usa', label: 'USA', icon: null, emoji: 'US', count: regionStats?.usa },
              { id: 'other', label: 'Worldwide', icon: <Globe className="w-4 h-4" />, count: regionStats?.other },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleRegionChange(tab.id)}
                data-testid={`region-tab-${tab.id}`}
                className={`flex items-center gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-xl text-xs sm:text-sm font-medium transition-all duration-200 whitespace-nowrap shrink-0 ${
                  regionFilter === tab.id
                    ? 'bg-indigo-500 dark:bg-cyan-500/15 text-white dark:text-cyan-400 shadow-lg shadow-indigo-500/25 dark:shadow-none dark:border dark:border-cyan-500/30'
                    : 'bg-white dark:bg-white/[0.03] text-zinc-600 dark:text-zinc-500 border border-zinc-200 dark:border-white/[0.06] hover:border-indigo-300 dark:hover:border-white/[0.15] hover:text-indigo-600 dark:hover:text-zinc-300'
                }`}
              >
                {tab.icon || <span className="text-xs font-bold">{tab.emoji}</span>}
                {tab.label}
                {tab.count != null && (
                  <span className={`px-1.5 py-0.5 rounded-md text-xs ${
                    regionFilter === tab.id ? 'bg-white/20 text-white dark:bg-cyan-500/20 dark:text-cyan-300' : 'bg-zinc-100 dark:bg-white/[0.04] text-zinc-500 dark:text-zinc-500'
                  }`}>{tab.count}</span>
                )}
              </button>
            ))}
          </div>

          {/* Batch Action Bar */}
          {batchSelectMode && (
            <div className="bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-200 dark:border-indigo-500/20 rounded-2xl p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="batch-action-bar">
              <div className="flex items-center gap-3 flex-wrap">
                <span className="text-sm font-medium text-indigo-700">
                  {batchSelectedJobs.length} job{batchSelectedJobs.length !== 1 ? 's' : ''} selected
                </span>
                <Button size="sm" variant="ghost" onClick={selectAllVisible} className="text-indigo-600 hover:bg-indigo-100 text-xs" data-testid="select-all-btn">
                  Select All ({Math.min(filteredJobs.length, 100)})
                </Button>
                {batchSelectedJobs.length > 0 && (
                  <Button size="sm" variant="ghost" onClick={() => setBatchSelectedJobs([])} className="text-zinc-500 hover:bg-zinc-100 text-xs">
                    Clear
                  </Button>
                )}
              </div>
              <div className="flex items-center gap-2 flex-wrap w-full sm:w-auto">
                {bulkApplyProgress.active && (
                  <div className="flex items-center gap-2 mr-1 sm:mr-3">
                    <Loader2 className="w-4 h-4 animate-spin text-indigo-500" />
                    <span className="text-xs sm:text-sm text-indigo-700 font-medium">{bulkApplyProgress.done}/{bulkApplyProgress.total} applied</span>
                    <div className="w-16 sm:w-24 h-2 bg-indigo-200 rounded-full overflow-hidden">
                      <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${(bulkApplyProgress.done / bulkApplyProgress.total) * 100}%` }} />
                    </div>
                  </div>
                )}
                <Button size="sm" onClick={handleBatchOptimize} disabled={batchSelectedJobs.length < 2 || bulkApplying} className="rounded-xl bg-white text-indigo-600 border border-indigo-200 hover:bg-indigo-100 flex-1 sm:flex-initial" data-testid="batch-optimize-btn">
                  <Wand2 className="w-4 h-4 mr-1" /> <span className="hidden xs:inline">Batch </span>Optimize CV
                </Button>
                <Button size="sm" onClick={handleBulkApply} disabled={batchSelectedJobs.length === 0 || bulkApplying} className="rounded-xl bg-indigo-500 hover:bg-indigo-600 text-white flex-1 sm:flex-initial" data-testid="bulk-apply-btn">
                  {bulkApplying ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}
                  Apply<span className="hidden sm:inline"> to All</span> ({batchSelectedJobs.length})
                </Button>
              </div>
            </div>
          )}

          {/* Burst Auto-Apply — hidden in v1 (no automatic submissions). Code kept for a future release. */}
          {SHOW_BURST_AUTO_APPLY && ((user?.subscription_plan === 'power' || user?.subscription_plan === 'unlimited' || user?.is_founder) ? (
            <div className="bg-gradient-to-r from-amber-500/10 via-orange-500/10 to-rose-500/10 border border-amber-500/30 rounded-2xl p-4 sm:p-5 mb-6 shadow-soft" data-testid="burst-auto-apply-panel">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-xl bg-amber-500/20 border border-amber-500/30 flex items-center justify-center text-amber-400 flex-shrink-0">
                    <Zap className="w-5 h-5" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-sm sm:text-base font-bold text-zinc-900 dark:text-white">One-click auto-apply</h3>
                    <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">Pick a target and we&apos;ll auto-apply to that many top-matched jobs in the background.</p>
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2 sm:flex sm:flex-wrap sm:justify-end">
                  {[50, 100, 200, 500].map((n) => (
                    <Button
                      key={n}
                      onClick={() => handleBurstApply(n)}
                      disabled={burstApplying !== null}
                      className={`rounded-xl font-semibold ${
                        n === 500
                          ? 'bg-rose-500 hover:bg-rose-600 text-white shadow-lg shadow-rose-500/20'
                          : n === 200
                          ? 'bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/20'
                          : 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg shadow-amber-500/20'
                      } ${burstApplying === n ? 'opacity-80' : ''}`}
                      data-testid={`burst-apply-${n}-btn`}
                    >
                      {burstApplying === n ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <Zap className="w-4 h-4 mr-1" />
                          {n}
                        </>
                      )}
                    </Button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <div className="bg-zinc-50 dark:bg-white/[0.02] border border-dashed border-zinc-200 dark:border-white/[0.06] rounded-2xl p-4 sm:p-5 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3" data-testid="burst-auto-apply-locked">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/15 border border-amber-500/25 flex items-center justify-center text-amber-500 flex-shrink-0">
                  <Zap className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="text-sm sm:text-base font-bold text-zinc-900 dark:text-white">Auto-apply to 50, 100, 200 or 500 jobs in one click</h3>
                  <p className="text-xs text-zinc-600 dark:text-zinc-400 mt-0.5">Available on Power and Unlimited plans.</p>
                </div>
              </div>
              <Button
                onClick={() => navigate('/subscription')}
                className="rounded-xl bg-amber-500 hover:bg-amber-600 text-white"
                data-testid="burst-auto-apply-upgrade-btn"
              >
                Unlock Auto-Apply
              </Button>
            </div>
          ))}

          {/* Search and Filters */}
          <div className="bg-white dark:bg-[#1c1c1c] rounded-2xl border border-zinc-100 dark:border-white/[0.08] p-3 sm:p-4 mb-6 shadow-soft">
            <div className="flex flex-col gap-3 sm:gap-4">
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <Input
                    placeholder="Search jobs, companies, cities..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-12 h-10 sm:h-12 rounded-xl border-zinc-200 text-sm"
                    data-testid="job-search-input"
                  />
                </div>
                <div className="relative flex-1">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-zinc-400" />
                  <Input
                    placeholder="Location..."
                    value={locationFilter}
                    onChange={(e) => setLocationFilter(e.target.value)}
                    className="pl-12 h-10 sm:h-12 rounded-xl border-zinc-200 text-sm"
                    data-testid="location-filter-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 sm:gap-4">
              <Select value={sourceFilter} onValueChange={setSourceFilter}>
                <SelectTrigger className="w-full lg:w-48 h-10 sm:h-12 rounded-xl border-zinc-200 text-xs sm:text-sm" data-testid="source-filter">
                  <SelectValue placeholder="All Sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sources</SelectItem>
                  <SelectItem value="linkedin">LinkedIn</SelectItem>
                  <SelectItem value="indeed">Indeed</SelectItem>
                  <SelectItem value="glassdoor">Glassdoor</SelectItem>
                  <SelectItem value="google">Google Jobs</SelectItem>
                  <SelectItem value="ziprecruiter">ZipRecruiter</SelectItem>
                  <SelectItem value="adzuna">Adzuna</SelectItem>
                  <SelectItem value="remotive">Remotive</SelectItem>
                  <SelectItem value="remoteok">RemoteOK</SelectItem>
                  <SelectItem value="dice">Dice</SelectItem>
                  <SelectItem value="monster">Monster</SelectItem>
                </SelectContent>
              </Select>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className="w-full lg:w-48 h-10 sm:h-12 rounded-xl border-zinc-200 text-xs sm:text-sm" data-testid="category-filter">
                  <SelectValue placeholder="All Categories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  <SelectItem value="hospitality">Hospitality & Food Service</SelectItem>
                  <SelectItem value="modeling">Modeling & Creative</SelectItem>
                  <SelectItem value="Sales">Sales</SelectItem>
                  <SelectItem value="Crypto & Web3">Crypto & Web3</SelectItem>
                  <SelectItem value="Fintech">Fintech</SelectItem>
                  <SelectItem value="Data Science">Data Science</SelectItem>
                  <SelectItem value="Engineering">Engineering</SelectItem>
                  <SelectItem value="Design & Product">Design & Product</SelectItem>
                  <SelectItem value="Management">Management</SelectItem>
                </SelectContent>
              </Select>
              <Select value={postedWithin} onValueChange={setPostedWithin}>
                <SelectTrigger className="w-full lg:w-48 h-10 sm:h-12 rounded-xl border-zinc-200 text-xs sm:text-sm" data-testid="posted-within-filter">
                  <SelectValue placeholder="Any Time" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Any Time</SelectItem>
                  <SelectItem value="1h">Last Hour</SelectItem>
                  <SelectItem value="24h">Last 24 Hours</SelectItem>
                  <SelectItem value="7d">Last Week</SelectItem>
                  <SelectItem value="30d">Last 30 Days</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="h-10 sm:h-12 rounded-xl lg:w-auto col-span-2 sm:col-span-1 text-xs sm:text-sm">
                <Filter className="w-4 h-4 mr-2" />
                Filters
              </Button>
              </div>
            </div>

            {/* Advanced Filters */}
            {showFilters && (
              <motion.div initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} className="mt-4 pt-4 border-t border-zinc-100">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <Label className="text-sm font-medium text-zinc-700 mb-2 block">Company</Label>
                    <Input
                      placeholder="Filter by company..."
                      value={companyFilter}
                      onChange={(e) => setCompanyFilter(e.target.value)}
                      className="h-10 rounded-xl border-zinc-200"
                    />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-zinc-700 mb-2 block">Salary Range</Label>
                    <div className="px-2">
                      <Slider
                        value={salaryRange}
                        onValueChange={setSalaryRange}
                        min={0}
                        max={300000}
                        step={10000}
                        className="mt-2"
                      />
                      <div className="flex justify-between text-xs text-zinc-500 mt-1">
                        <span>${(salaryRange[0] / 1000).toFixed(0)}k</span>
                        <span>${(salaryRange[1] / 1000).toFixed(0)}k</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Switch id="remote" checked={remoteOnly} onCheckedChange={setRemoteOnly} />
                    <Label htmlFor="remote" className="text-sm font-medium text-zinc-700">Remote Only</Label>
                  </div>
                </div>
              </motion.div>
            )}
          </div>

          {/* Jobs Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="w-8 h-8 border-4 border-indigo-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : filteredJobs.length > 0 ? (
            <>
              {/* Mobile swipe hint */}
              <div className="md:hidden flex items-center justify-center gap-2 mb-4 text-xs text-zinc-400 dark:text-zinc-500" data-testid="swipe-hint">
                <span>Swipe right to apply</span>
                <span className="text-zinc-300 dark:text-zinc-600">|</span>
                <span>Swipe left to skip</span>
              </div>
              <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-6">
                {filteredJobs.map((job) => (
                  <JobCard key={job.id} job={job} onApply={handleApply} onOptimize={handleOptimize} onViewDetail={(j) => { setFullDesc({ loading: false, text: null, source: null }); setJobDetail({ open: true, job: j }); }} onDismiss={handleDismissJob} cvs={cvs} batchMode={batchSelectMode} batchSelected={batchSelectedJobs.some(j => j.id === job.id)} onBatchToggle={toggleBatchJob} usageData={usageData} />
                ))}
              </div>
              {hasMore && (
                <div className="text-center mt-8">
                  <Button
                    onClick={loadMore}
                    disabled={loadingMore}
                    variant="outline"
                    className="px-8 py-6 rounded-xl border-zinc-200 hover:border-indigo-300 hover:bg-indigo-50 text-zinc-700"
                    data-testid="load-more-btn"
                  >
                    {loadingMore ? (
                      <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Loading more jobs...</>
                    ) : (
                      <>Load More Jobs ({jobs.length} shown)</>
                    )}
                  </Button>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <Briefcase className="w-16 h-16 mx-auto mb-4 text-zinc-300" />
              <p className="text-zinc-500 text-lg">No jobs found matching your criteria</p>
            </div>
          )}
      </div>

      {/* Batch Selection Floating Bar */}
      {batchSelectMode && batchSelectedJobs.length > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-zinc-900 text-white px-6 py-3 rounded-2xl shadow-2xl flex items-center gap-4" data-testid="batch-floating-bar">
          <span className="text-sm font-medium">{batchSelectedJobs.length} job{batchSelectedJobs.length > 1 ? 's' : ''} selected</span>
          <div className="w-px h-5 bg-zinc-700" />
          <div className="flex gap-1 max-w-md overflow-hidden">
            {batchSelectedJobs.slice(0, 3).map((j, i) => (
              <span key={i} className="text-xs bg-zinc-800 px-2 py-1 rounded-lg truncate max-w-[120px]">{j.job_title}</span>
            ))}
            {batchSelectedJobs.length > 3 && <span className="text-xs text-zinc-400">+{batchSelectedJobs.length - 3} more</span>}
          </div>
          <Button
            onClick={handleBatchOptimize}
            disabled={batchSelectedJobs.length < 2}
            className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-sm"
            data-testid="batch-optimize-btn"
          >
            <Sparkles className="w-4 h-4 mr-2" /> Optimize for All
          </Button>
        </div>
      )}

      {/* Batch Optimization Results Dialog */}
      <Dialog open={batchDialog.open} onOpenChange={(open) => { if (!open) { setBatchDialog({ open: false, result: null, expandedCell: null }); setBatchSelectedJobs([]); } }}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-indigo-500" />
              Batch CV Optimization
            </DialogTitle>
            <DialogDescription>
              {batchDialog.result?.status === 'complete'
                ? `Comparison matrix for ${batchDialog.result?.total_jobs || 0} jobs`
                : `Processing ${batchDialog.result?.completed_jobs || 0} of ${batchDialog.result?.total_jobs || '...'} jobs...`}
            </DialogDescription>
          </DialogHeader>

          <div className="py-2">
            {!batchDialog.result || batchDialog.result.status === 'generating' ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="batch-loading">
                <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                <span className="text-zinc-600">
                  {batchDialog.result ? `Processing job ${batchDialog.result.completed_jobs + 1} of ${batchDialog.result.total_jobs}...` : 'Starting batch optimization...'}
                </span>
                {batchDialog.result?.completed_jobs > 0 && (
                  <div className="w-48 bg-zinc-200 rounded-full h-2">
                    <div className="bg-indigo-500 h-2 rounded-full transition-all" style={{ width: `${(batchDialog.result.completed_jobs / batchDialog.result.total_jobs) * 100}%` }} />
                  </div>
                )}
              </div>
            ) : (
              <div data-testid="batch-matrix">
                {/* Matrix Header */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr>
                        <th className="text-left p-2 text-xs font-semibold text-zinc-500 uppercase w-36 sticky left-0 bg-white">Strategy</th>
                        {(batchDialog.result.jobs || []).map((job, jIdx) => (
                          <th key={jIdx} className="p-2 text-center min-w-[140px]">
                            <div className="text-sm font-semibold text-zinc-800 truncate">{job.job_title}</div>
                            <div className="text-[10px] text-zinc-400 truncate">{job.company}</div>
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {['Skills-Focused', 'Achievement-Focused', 'Keyword-Optimized', 'Executive Summary'].map((strategy, sIdx) => {
                        const rowColors = ['bg-blue-50 hover:bg-blue-100', 'bg-emerald-50 hover:bg-emerald-100', 'bg-amber-50 hover:bg-amber-100', 'bg-purple-50 hover:bg-purple-100'];
                        const scoreColors = (score) => score >= 80 ? 'text-green-700 bg-green-100' : score >= 60 ? 'text-amber-700 bg-amber-100' : 'text-red-700 bg-red-100';
                        return (
                          <tr key={sIdx} className={`${rowColors[sIdx]} transition-colors`}>
                            <td className="p-2 text-xs font-semibold text-zinc-700 sticky left-0" style={{ background: 'inherit' }}>{strategy}</td>
                            {(batchDialog.result.jobs || []).map((job, jIdx) => {
                              const version = (job.versions || []).find(v => v.label === strategy);
                              const cellKey = `${sIdx}-${jIdx}`;
                              const isExpanded = batchDialog.expandedCell === cellKey;
                              return (
                                <td key={jIdx} className="p-1.5 text-center">
                                  {job.status === 'failed' ? (
                                    <span className="text-xs text-red-400">Failed</span>
                                  ) : !version ? (
                                    <span className="text-xs text-zinc-300">—</span>
                                  ) : (
                                    <div>
                                      <button
                                        onClick={() => setBatchDialog(prev => ({ ...prev, expandedCell: isExpanded ? null : cellKey }))}
                                        className={`w-full px-3 py-2 rounded-lg transition-all ${isExpanded ? 'ring-2 ring-indigo-300' : ''}`}
                                        data-testid={`batch-cell-${sIdx}-${jIdx}`}
                                      >
                                        <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${scoreColors(version.match_score)}`}>
                                          {version.match_score}%
                                        </span>
                                      </button>
                                      {isExpanded && (
                                        <div className="mt-2 p-3 bg-white rounded-lg border border-zinc-200 text-left space-y-2" data-testid={`batch-cell-expanded-${sIdx}-${jIdx}`}>
                                          <p className="text-xs text-zinc-500">{version.strategy}</p>
                                          {version.summary && <p className="text-xs text-zinc-700">{version.summary}</p>}
                                          {version.skills?.length > 0 && (
                                            <div className="flex flex-wrap gap-1">
                                              {version.skills.slice(0, 6).map((s, i) => (
                                                <span key={i} className="px-1.5 py-0.5 bg-zinc-100 rounded text-[10px] text-zinc-600">{s}</span>
                                              ))}
                                            </div>
                                          )}
                                          <div className="flex gap-1.5 pt-1">
                                            <Button
                                              onClick={() => {
                                                const optEntry = batchDialog.result.jobs[jIdx];
                                                handleSaveOptimizedCV(optEntry.optimization_id, version.id, version.label);
                                              }}
                                              size="sm"
                                              className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-[10px] h-7"
                                              data-testid={`batch-save-${sIdx}-${jIdx}`}
                                            >
                                              <Save className="w-3 h-3 mr-1" /> Save
                                            </Button>
                                            <Button
                                              onClick={() => {
                                                const optEntry = batchDialog.result.jobs[jIdx];
                                                handleDownloadOptimizedPDF(optEntry.optimization_id, version.id, version.label);
                                              }}
                                              variant="outline"
                                              size="sm"
                                              className="rounded-lg text-[10px] h-7 border-zinc-300"
                                              data-testid={`batch-download-${sIdx}-${jIdx}`}
                                            >
                                              <Download className="w-3 h-3 mr-1" /> PDF
                                            </Button>
                                          </div>
                                        </div>
                                      )}
                                    </div>
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Best Match Summary */}
                {(() => {
                  let bestScore = 0, bestJob = '', bestStrategy = '';
                  (batchDialog.result.jobs || []).forEach(job => {
                    (job.versions || []).forEach(v => {
                      if (v.match_score > bestScore) { bestScore = v.match_score; bestJob = job.job_title; bestStrategy = v.label; }
                    });
                  });
                  if (!bestScore) return null;
                  return (
                    <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-xl" data-testid="batch-best-match">
                      <p className="text-sm text-green-800">
                        <span className="font-semibold">Best match:</span> {bestStrategy} for {bestJob} — <span className="font-bold">{bestScore}%</span>
                      </p>
                    </div>
                  );
                })()}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>


      {/* Job Detail Dialog */}
      <Dialog open={jobDetail.open} onOpenChange={(open) => setJobDetail({ ...jobDetail, open })}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto pb-6 dark:bg-[#181818] dark:border-white/[0.08]">
          {jobDetail.job && (
            <>
              <DialogHeader>
                <div className="flex items-start gap-4">
                  <CompanyLogo company={jobDetail.job.company} size="md" />
                  <div className="min-w-0 flex-1">
                    <DialogTitle className="font-heading text-xl sm:text-2xl dark:text-white leading-tight">
                      {jobDetail.job.job_title}
                    </DialogTitle>
                    <DialogDescription className="dark:text-zinc-400 mt-1">
                      <span className="font-medium text-zinc-700 dark:text-zinc-300">{jobDetail.job.company}</span>
                    </DialogDescription>
                  </div>
                </div>
              </DialogHeader>

              <div className="space-y-5 pt-2">
                {/* Meta info pills */}
                <div className="flex flex-wrap gap-2 text-sm">
                  {jobDetail.job.location && (
                    <span className="inline-flex items-center gap-1.5 bg-zinc-100 dark:bg-white/[0.06] text-zinc-700 dark:text-zinc-300 px-3 py-1.5 rounded-xl" data-testid="detail-location">
                      <MapPin className="w-3.5 h-3.5 text-zinc-400" /> {jobDetail.job.location}
                    </span>
                  )}
                  {jobDetail.job.is_remote && (
                    <span className="inline-flex items-center gap-1.5 bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-400 px-3 py-1.5 rounded-xl">
                      <Globe className="w-3.5 h-3.5" /> Remote
                    </span>
                  )}
                  {jobDetail.job.employment_type && jobDetail.job.employment_type !== 'None' && (
                    <span className="inline-flex items-center gap-1.5 bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-400 px-3 py-1.5 rounded-xl">
                      <Briefcase className="w-3.5 h-3.5" /> {jobDetail.job.employment_type}
                    </span>
                  )}
                  {jobDetail.job.salary_range && jobDetail.job.salary_range !== 'None' && (
                    <span className="inline-flex items-center gap-1.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 px-3 py-1.5 rounded-xl">
                      <DollarSign className="w-3.5 h-3.5" /> {jobDetail.job.salary_range}
                    </span>
                  )}
                  {jobDetail.job.posted_at && (
                    <span className="inline-flex items-center gap-1.5 bg-zinc-100 dark:bg-white/[0.06] text-zinc-600 dark:text-zinc-400 px-3 py-1.5 rounded-xl">
                      <Clock className="w-3.5 h-3.5" /> Posted {(() => {
                        const d = new Date(jobDetail.job.posted_at);
                        const h = Math.floor((new Date() - d) / 3600000);
                        return h < 24 ? `${h}h ago` : `${Math.floor(h/24)}d ago`;
                      })()}
                    </span>
                  )}
                  {jobDetail.job.source && (
                    <span className="text-xs font-medium text-zinc-400 dark:text-zinc-500 bg-zinc-50 dark:bg-white/[0.04] px-2.5 py-1.5 rounded-xl border border-zinc-100 dark:border-white/[0.08] capitalize">
                      {jobDetail.job.source}
                    </span>
                  )}
                </div>

                {/* Match score + skills */}
                {jobDetail.job.match_score != null && jobDetail.job.match_score > 0 && (
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-zinc-50 dark:bg-white/[0.04] border border-zinc-100 dark:border-white/[0.08]">
                    <div className={`px-3 py-1.5 rounded-full text-sm font-bold ${
                      jobDetail.job.match_score >= 80 ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400'
                      : jobDetail.job.match_score >= 60 ? 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-400'
                      : jobDetail.job.match_score >= 40 ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-400'
                      : 'bg-zinc-200 text-zinc-600 dark:bg-white/10 dark:text-zinc-400'
                    }`} data-testid="detail-match-score">
                      {jobDetail.job.match_score}% Match
                    </div>
                    {jobDetail.job.matched_skills?.length > 0 && (
                      <div className="flex flex-wrap gap-1.5">
                        {jobDetail.job.matched_skills.map((skill, i) => (
                          <span key={i} className="px-2 py-0.5 bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400 border border-emerald-200 dark:border-emerald-500/20 rounded-full text-xs capitalize">{skill}</span>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {/* Full job description */}
                <div data-testid="job-detail-description">
                  <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4 text-zinc-400" /> Job Description
                  </h3>
                  <div className="text-sm text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-line bg-zinc-50 dark:bg-white/[0.03] rounded-xl p-4 border border-zinc-100 dark:border-white/[0.06] max-h-[28rem] overflow-y-auto">
                    {fullDesc.text || stripHtml(jobDetail.job.description)}
                  </div>
                  {!fullDesc.text && (
                    <div className="mt-2 flex items-center justify-between flex-wrap gap-2">
                      <p className="text-[11px] text-zinc-500">
                        Showing a preview. Tap below to load the full posting in-app.
                      </p>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={fullDesc.loading}
                        data-testid="load-full-description-btn"
                        onClick={async () => {
                          if (!jobDetail.job?.id) return;
                          setFullDesc({ loading: true, text: null, source: null });
                          try {
                            const res = await axios.get(
                              `${API}/jobs/${jobDetail.job.id}/full-description`,
                              { headers: getAuthHeaders() }
                            );
                            const fullText = res.data?.description || stripHtml(jobDetail.job.description);
                            setFullDesc({
                              loading: false,
                              text: fullText,
                              source: res.data?.fetched_from_source ? 'live' : 'cached',
                            });
                          } catch (e) {
                            setFullDesc({ loading: false, text: null, source: null });
                            toast.error('Could not load full description. Try the job link below.');
                          }
                        }}
                      >
                        {fullDesc.loading ? (
                          <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> Loading full description…</>
                        ) : (
                          <>Load full description</>
                        )}
                      </Button>
                    </div>
                  )}
                  {fullDesc.text && fullDesc.source === 'live' && (
                    <p className="mt-2 text-[11px] text-emerald-500">
                      ✓ Loaded full description directly from the source posting.
                    </p>
                  )}
                </div>

                {/* Requirements */}
                {jobDetail.job.requirements?.length > 0 && (
                  <div data-testid="job-detail-requirements">
                    <h3 className="text-sm font-semibold text-zinc-900 dark:text-white mb-2 flex items-center gap-2">
                      <Target className="w-4 h-4 text-zinc-400" /> Requirements
                    </h3>
                    <ul className="space-y-1.5 text-sm text-zinc-700 dark:text-zinc-300">
                      {jobDetail.job.requirements.map((req, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-indigo-500 shrink-0 mt-0.5" />
                          <span>{req}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Missing skills warning */}
                {jobDetail.job.missing_skills?.length > 0 && (
                  <div className="p-3 rounded-xl bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/20">
                    <p className="text-xs font-semibold text-amber-800 dark:text-amber-400 mb-2">Skills you may want to highlight:</p>
                    <div className="flex flex-wrap gap-1.5">
                      {jobDetail.job.missing_skills.map((skill, i) => (
                        <span key={i} className="px-2 py-0.5 bg-amber-100 dark:bg-amber-500/15 text-amber-700 dark:text-amber-400 border border-amber-200 dark:border-amber-500/20 rounded-full text-xs capitalize">{skill}</span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button
                    onClick={() => { setJobDetail({ open: false, job: null }); handleApply(jobDetail.job); }}
                    className="sm:flex-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl"
                    data-testid="detail-apply-btn"
                  >
                    {jobDetail.job.source === 'linkedin' ? (
                      <><Wand2 className="w-4 h-4 mr-2" /> Tailor & Apply</>
                    ) : (
                      <><Wand2 className="w-4 h-4 mr-2" /> Tailor & Apply</>
                    )}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { setJobDetail({ open: false, job: null }); handleOptimize(jobDetail.job); }}
                    className="sm:flex-1 rounded-xl border-indigo-200 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                    data-testid="detail-optimize-btn"
                  >
                    <Wand2 className="w-4 h-4 mr-2" /> Adapt CV
                  </Button>
                  {jobDetail.job.url && (
                    <Button
                      variant="outline"
                      onClick={() => window.open(jobDetail.job.url, '_blank')}
                      className="sm:flex-1 rounded-xl dark:border-white/[0.1] dark:text-zinc-300 dark:hover:bg-white/[0.04]"
                      data-testid="detail-external-link-btn"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" /> View Original
                    </Button>
                  )}
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* Apply Dialog */}
      <Dialog open={applyDialog.open} onOpenChange={(open) => setApplyDialog({ ...applyDialog, open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto pb-6 dark:bg-[#181818] dark:border-white/[0.08]">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl dark:text-white">
              Prepare your application
            </DialogTitle>
            <DialogDescription className="dark:text-zinc-400">
              {applyDialog.job?.job_title} at {applyDialog.job?.company}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-5 py-4 pb-2">
            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Select CV</label>
              <Select value={selectedCv} onValueChange={setSelectedCv}>
                <SelectTrigger className="w-full h-12 rounded-xl dark:bg-white/[0.04] dark:border-white/[0.1] dark:text-white" data-testid="cv-select">
                  <SelectValue placeholder="Select a CV" />
                </SelectTrigger>
                <SelectContent className="dark:bg-[#1e1e1e] dark:border-white/[0.1]">
                  {cvs.map((cv) => (
                    <SelectItem key={cv.id} value={cv.id} className="dark:text-white dark:focus:bg-white/[0.06]">{cv.filename}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">Cover Letter Tone</label>
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-3" data-testid="tone-selector">
                {[
                  { id: 'professional', label: 'Professional', desc: 'Formal & confident' },
                  { id: 'diplomatic', label: 'Diplomatic', desc: 'Thoughtful & balanced' },
                  { id: 'warm', label: 'Warm', desc: 'Friendly & personable' },
                  { id: 'creative', label: 'Creative', desc: 'Bold & engaging' },
                  { id: 'concise', label: 'Concise', desc: 'Brief & direct' },
                ].map((tone) => (
                  <button
                    key={tone.id}
                    onClick={() => setSelectedTone(tone.id)}
                    data-testid={`tone-${tone.id}`}
                    className={`w-full px-2 py-2 rounded-lg text-xs font-medium transition-all border text-center truncate ${
                      selectedTone === tone.id
                        ? 'bg-indigo-500 text-white border-indigo-500 shadow-sm'
                        : 'bg-white dark:bg-white/[0.04] text-zinc-600 dark:text-zinc-400 border-zinc-200 dark:border-white/[0.1] hover:border-indigo-300 dark:hover:border-indigo-500/30 hover:text-indigo-600 dark:hover:text-indigo-400'
                    }`}
                    title={tone.desc}
                  >
                    {tone.label}
                  </button>
                ))}
              </div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-zinc-700 dark:text-zinc-300">Cover Letter</label>
                <Button variant="ghost" size="sm" onClick={() => generateCoverLetter()} disabled={generating || !selectedCv}
                  className="text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 hover:bg-indigo-50 dark:hover:bg-indigo-500/10" data-testid="generate-cover-letter-btn">
                  {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
                  {generating ? 'Generating...' : 'Regenerate'}
                </Button>
              </div>
              <textarea
                value={coverLetter}
                onChange={(e) => setCoverLetter(e.target.value)}
                placeholder={generating ? "Generating your cover letter with AI..." : "Your AI-generated cover letter will appear here..."}
                className="w-full h-40 sm:h-48 p-4 rounded-xl border border-zinc-200 dark:border-white/[0.1] dark:bg-white/[0.04] dark:text-white focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100 dark:focus:ring-indigo-500/20 resize-none text-sm"
                data-testid="cover-letter-textarea"
              />
            </div>

            <div className={`p-3 sm:p-4 rounded-xl text-sm ${applyDialog.job?.source === 'linkedin' ? 'bg-blue-50 dark:bg-blue-500/10 text-blue-800 dark:text-blue-300' : 'bg-teal-50 dark:bg-teal-500/10 text-teal-800 dark:text-teal-300'}`}>
              {applyDialog.job?.source === 'linkedin' ? (
                <p><strong>How it works:</strong> We tailor your CV and cover letter for this role. You review them, then complete the application on the employer's site — this uses 1 AI tailoring.</p>
              ) : (
                <p><strong>How it works:</strong> We tailor your CV and cover letter for this role. You review them, then complete the application on the employer's site — this uses 1 AI tailoring.</p>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={() => setApplyDialog({ open: false, job: null })} className="sm:flex-1 rounded-xl dark:border-white/[0.1] dark:text-zinc-300 dark:hover:bg-white/[0.04]">Cancel</Button>
              <Button 
                variant="outline" 
                onClick={() => { setApplyDialog({ open: false, job: null }); handleOptimize(applyDialog.job); }} 
                disabled={!selectedCv} 
                className="sm:flex-1 rounded-xl border-indigo-200 dark:border-indigo-500/20 text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-500/10"
                data-testid="adapt-cv-btn"
              >
                <Wand2 className="w-4 h-4 mr-2" />
                Adapt CV
              </Button>
              <Button onClick={submitApplication} disabled={!selectedCv || generating} className="sm:flex-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl" data-testid="submit-application-btn">
                {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : applyDialog.job?.source === 'linkedin' ? <MousePointer className="w-4 h-4 mr-2" /> : <Zap className="w-4 h-4 mr-2" />}
                Prepare Application
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>


      {/* Application Confirmation Dialog */}
      <Dialog open={confirmationDialog.open} onOpenChange={(open) => setConfirmationDialog({ ...confirmationDialog, open })}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto pb-6 dark:bg-[#181818] dark:border-white/[0.08]">
          {confirmationDialog.data && (
            <>
              <div className="text-center pt-2">
                <div className="w-12 h-12 sm:w-16 sm:h-16 bg-emerald-100 dark:bg-emerald-500/15 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Check className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600 dark:text-emerald-400" strokeWidth={3} />
                </div>
                <DialogTitle className="font-heading text-lg sm:text-xl dark:text-white mb-1">Your tailored application is ready</DialogTitle>
                <DialogDescription className="dark:text-zinc-400 text-sm">
                  Review your materials, then complete the application on the employer's site. We'll track it for you.
                </DialogDescription>
              </div>

              <div className="mt-3 bg-zinc-50 dark:bg-white/[0.04] rounded-xl border border-zinc-100 dark:border-white/[0.08] divide-y divide-zinc-100 dark:divide-white/[0.06]" data-testid="confirmation-receipt">
                <div className="flex justify-between items-center px-3 sm:px-4 py-2.5">
                  <span className="text-xs text-zinc-500 dark:text-zinc-400 shrink-0">Position</span>
                  <span className="text-xs sm:text-sm font-semibold text-zinc-900 dark:text-white text-right max-w-[65%] truncate">{confirmationDialog.data.job_title}</span>
                </div>
                <div className="flex justify-between items-center px-3 sm:px-4 py-2.5">
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">Company</span>
                  <span className="text-xs sm:text-sm font-medium text-zinc-700 dark:text-zinc-300">{confirmationDialog.data.company}</span>
                </div>
                {confirmationDialog.data.location && (
                  <div className="flex justify-between items-center px-3 sm:px-4 py-2.5">
                    <span className="text-xs text-zinc-500 dark:text-zinc-400">Location</span>
                    <span className="text-xs sm:text-sm text-zinc-700 dark:text-zinc-300 text-right max-w-[65%] truncate">{confirmationDialog.data.location}</span>
                  </div>
                )}
                <div className="flex justify-between items-center px-3 sm:px-4 py-2.5">
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">CV Used</span>
                  <span className="text-xs sm:text-sm text-zinc-700 dark:text-zinc-300 flex items-center gap-1.5 max-w-[65%] truncate">
                    <FileText className="w-3.5 h-3.5 text-indigo-500 shrink-0" />
                    <span className="truncate">{confirmationDialog.data.cv_filename}</span>
                  </span>
                </div>
                <div className="flex justify-between items-center px-3 sm:px-4 py-2.5">
                  <span className="text-xs text-zinc-500 dark:text-zinc-400">Status</span>
                  <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10 px-2.5 py-1 rounded-full capitalize">{confirmationDialog.data.status}</span>
                </div>
              </div>

              {confirmationDialog.data.cover_letter_preview && (
                <div className="mt-3 p-3 rounded-xl bg-indigo-50 dark:bg-indigo-500/10 border border-indigo-100 dark:border-indigo-500/20">
                  <p className="text-xs font-semibold text-indigo-700 dark:text-indigo-400 mb-1">Cover Letter</p>
                  <p className="text-xs text-indigo-600 dark:text-indigo-300 leading-relaxed italic line-clamp-3">
                    {confirmationDialog.data.cover_letter_preview}
                  </p>
                </div>
              )}

              <div className="flex gap-3 mt-4">
                <Button
                  variant="outline"
                  onClick={() => { setConfirmationDialog({ open: false, data: null }); window.location.href = '/applications'; }}
                  className="flex-1 rounded-xl text-xs sm:text-sm dark:border-white/[0.1] dark:text-zinc-300"
                  data-testid="view-applications-btn"
                >
                  View Applications
                </Button>
                <Button
                  onClick={() => { const u = confirmationDialog.data?.job_url; setConfirmationDialog({ open: false, data: null }); if (u) window.open(u, '_blank'); }}
                  className="flex-1 bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl text-xs sm:text-sm"
                  data-testid="continue-browsing-btn"
                >
                  {confirmationDialog.data?.job_url ? 'Apply on Company Site' : 'Continue'}
                </Button>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>


      {/* Optimize CV Dialog */}
      <Dialog open={optimizeDialog.open} onOpenChange={(open) => setOptimizeDialog({ ...optimizeDialog, open })}>
        <DialogContent className={`${showCompare ? 'max-w-4xl' : 'max-w-2xl'} max-h-[90vh] overflow-y-auto transition-all`}>
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl flex items-center gap-2">
              <Wand2 className="w-6 h-6 text-indigo-500" />
              CV Optimization
            </DialogTitle>
            <DialogDescription>
              AI-powered suggestions to optimize your CV for: {optimizeDialog.job?.job_title}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {!optimizeDialog.result ? (
              optimizeDialog.result?.status === 'failed' ? (
                <div className="text-center py-12" data-testid="optimize-failed">
                  <p className="text-red-500 font-medium mb-3">Optimization failed. Please try again.</p>
                  <Button onClick={() => setOptimizeDialog({ open: false, job: null, result: null, saving: null })} className="bg-zinc-800 text-white rounded-xl">Close</Button>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center py-12 gap-3" data-testid="optimize-loading">
                  <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
                  <span className="text-zinc-600">Generating 2 optimized versions...</span>
                  <span className="text-xs text-zinc-400">This may take up to 15 seconds</span>
                </div>
              )
            ) : (
              <div className="space-y-3" data-testid="optimize-versions-list">
                {/* Compare button bar */}
                {!showCompare && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-zinc-400">
                      {compareVersions.length === 0 ? 'Select 2 versions to compare side-by-side' : 
                       compareVersions.length === 1 ? 'Select 1 more to compare' : ''}
                    </p>
                    {compareVersions.length === 2 && (
                      <Button
                        onClick={() => setShowCompare(true)}
                        size="sm"
                        className="bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-xs"
                        data-testid="compare-versions-btn"
                      >
                        Compare Selected
                      </Button>
                    )}
                  </div>
                )}

                {/* Comparison View */}
                {showCompare && (() => {
                  const versions = optimizeDialog.result.versions || [];
                  const left = versions.find(v => v.id === compareVersions[0]);
                  const right = versions.find(v => v.id === compareVersions[1]);
                  if (!left || !right) return null;
                  const leftIdx = versions.indexOf(left);
                  const rightIdx = versions.indexOf(right);
                  const colorMap = {
                    0: { bg: 'bg-blue-50', border: 'border-blue-300', accent: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
                    1: { bg: 'bg-emerald-50', border: 'border-emerald-300', accent: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
                    2: { bg: 'bg-amber-50', border: 'border-amber-300', accent: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
                    3: { bg: 'bg-purple-50', border: 'border-purple-300', accent: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
                  };
                  const lc = colorMap[leftIdx] || colorMap[0];
                  const rc = colorMap[rightIdx] || colorMap[0];

                  const CompareColumn = ({ v, c, side }) => (
                    <div className={`flex-1 min-w-0 border ${c.border} rounded-xl overflow-hidden`} data-testid={`compare-column-${side}`}>
                      <div className={`p-3 ${c.bg} flex items-center justify-between`}>
                        <h4 className={`font-semibold text-sm ${c.accent} truncate`}>{v.label}</h4>
                        {v.match_score && <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${c.badge}`}>{v.match_score}%</span>}
                      </div>
                      <div className="p-3 space-y-3">
                        {v.summary && (
                          <div>
                            <h5 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-1">Summary</h5>
                            <p className="text-xs text-zinc-700 leading-relaxed">{v.summary}</p>
                          </div>
                        )}
                        {v.skills?.length > 0 && (
                          <div>
                            <h5 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-1">Skills</h5>
                            <div className="flex flex-wrap gap-1">
                              {v.skills.map((s, i) => (
                                <span key={i} className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${c.badge}`}>{s}</span>
                              ))}
                            </div>
                          </div>
                        )}
                        {v.experience_bullets?.length > 0 && (
                          <div>
                            <h5 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-1">Experience</h5>
                            <ul className="space-y-1">
                              {v.experience_bullets.map((b, i) => (
                                <li key={i} className="text-xs text-zinc-600 flex items-start gap-1.5">
                                  <Check className="w-3 h-3 text-green-500 mt-0.5 flex-shrink-0" />
                                  <span>{b}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                        {v.key_changes?.length > 0 && (
                          <div className={`p-2 rounded-lg ${c.bg}`}>
                            <h5 className="text-[10px] font-semibold text-zinc-400 uppercase tracking-wide mb-1">What Changed</h5>
                            {v.key_changes.map((ch, i) => (
                              <p key={i} className={`text-[10px] ${c.accent}`}>• {ch}</p>
                            ))}
                          </div>
                        )}
                        <div className="flex gap-1.5">
                        <Button
                          onClick={() => handleSaveOptimizedCV(optimizeDialog.result.id, v.id, v.label)}
                          disabled={optimizeDialog.saving === v.id}
                          size="sm"
                          className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded-lg text-xs"
                          data-testid={`compare-save-btn-${side}`}
                        >
                          {optimizeDialog.saving === v.id ? (
                            <><Loader2 className="w-3 h-3 animate-spin mr-1" /> Saving...</>
                          ) : (
                            <><Save className="w-3 h-3 mr-1" /> Save</>
                          )}
                        </Button>
                        <Button
                          onClick={() => handleDownloadOptimizedPDF(optimizeDialog.result.id, v.id, v.label)}
                          variant="outline"
                          size="sm"
                          className="rounded-lg text-xs border-zinc-300"
                          data-testid={`compare-download-btn-${side}`}
                        >
                          <Download className="w-3 h-3 mr-1" /> PDF
                        </Button>
                        </div>
                      </div>
                    </div>
                  );

                  return (
                    <div data-testid="compare-view">
                      <div className="flex items-center justify-between mb-3">
                        <h3 className="font-semibold text-sm text-zinc-800">Side-by-Side Comparison</h3>
                        <Button
                          onClick={() => setShowCompare(false)}
                          variant="ghost"
                          size="sm"
                          className="text-xs text-zinc-500 hover:text-zinc-800"
                          data-testid="compare-back-btn"
                        >
                          Back to all versions
                        </Button>
                      </div>
                      <div className="flex gap-3">
                        <CompareColumn v={left} c={lc} side="left" />
                        <CompareColumn v={right} c={rc} side="right" />
                      </div>
                    </div>
                  );
                })()}

                {/* Version Cards (hidden during compare) */}
                {!showCompare && (optimizeDialog.result.versions || []).map((version, idx) => {
                  const isExpanded = expandedVersion === version.id;
                  const isSelected = compareVersions.includes(version.id);
                  const colorMap = {
                    0: { bg: 'bg-blue-50', border: 'border-blue-200', accent: 'text-blue-700', badge: 'bg-blue-100 text-blue-700', icon: <Target className="w-5 h-5" /> },
                    1: { bg: 'bg-emerald-50', border: 'border-emerald-200', accent: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700', icon: <Award className="w-5 h-5" /> },
                    2: { bg: 'bg-amber-50', border: 'border-amber-200', accent: 'text-amber-700', badge: 'bg-amber-100 text-amber-700', icon: <Zap className="w-5 h-5" /> },
                    3: { bg: 'bg-purple-50', border: 'border-purple-200', accent: 'text-purple-700', badge: 'bg-purple-100 text-purple-700', icon: <FileText className="w-5 h-5" /> },
                  };
                  const c = colorMap[idx] || colorMap[0];

                  return (
                    <div key={version.id} className={`border ${isSelected ? 'border-indigo-400 ring-2 ring-indigo-100' : c.border} rounded-xl overflow-hidden transition-all`} data-testid={`optimize-version-card-${idx}`}>
                      {/* Card Header */}
                      <div className={`flex items-center ${c.bg} hover:brightness-95 transition-all`}>
                        {/* Compare checkbox */}
                        <button
                          className="pl-3 pr-1 py-4 flex items-center"
                          onClick={(e) => {
                            e.stopPropagation();
                            setCompareVersions(prev => {
                              if (prev.includes(version.id)) return prev.filter(id => id !== version.id);
                              if (prev.length >= 2) return [prev[1], version.id];
                              return [...prev, version.id];
                            });
                          }}
                          title={isSelected ? 'Remove from comparison' : 'Add to comparison'}
                          data-testid={`compare-checkbox-${idx}`}
                        >
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center transition-colors ${isSelected ? 'bg-indigo-500 border-indigo-500' : 'border-zinc-300 bg-white'}`}>
                            {isSelected && <Check className="w-3 h-3 text-white" />}
                          </div>
                        </button>

                        <button
                          className="flex-1 flex items-center justify-between p-4 pl-2"
                          onClick={() => setExpandedVersion(isExpanded ? null : version.id)}
                          data-testid={`optimize-version-toggle-${idx}`}
                        >
                          <div className="flex items-center gap-3">
                            <span className={c.accent}>{c.icon}</span>
                            <div className="text-left">
                              <h4 className={`font-semibold ${c.accent}`}>{version.label}</h4>
                              <p className="text-xs text-zinc-500">{version.strategy}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            {version.match_score && (
                              <span className={`text-sm font-bold px-2.5 py-1 rounded-full ${c.badge}`}>{version.match_score}%</span>
                            )}
                            {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
                          </div>
                        </button>
                      </div>

                      {/* Expanded Details */}
                      {isExpanded && (
                        <div className="p-4 space-y-4 border-t border-zinc-100 bg-white" data-testid={`optimize-version-details-${idx}`}>
                          {version.summary && (
                            <div>
                              <h5 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-1">Professional Summary</h5>
                              <p className="text-sm text-zinc-700 leading-relaxed">{version.summary}</p>
                            </div>
                          )}
                          {version.skills?.length > 0 && (
                            <div>
                              <h5 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Key Skills</h5>
                              <div className="flex flex-wrap gap-1.5">
                                {version.skills.map((skill, i) => (
                                  <span key={i} className={`px-2.5 py-1 rounded-full text-xs font-medium ${c.badge}`}>{skill}</span>
                                ))}
                              </div>
                            </div>
                          )}
                          {version.experience_bullets?.length > 0 && (
                            <div>
                              <h5 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">Experience Highlights</h5>
                              <ul className="space-y-1.5">
                                {version.experience_bullets.map((bullet, i) => (
                                  <li key={i} className="flex items-start gap-2 text-sm text-zinc-700">
                                    <Check className="w-3.5 h-3.5 text-green-500 mt-0.5 flex-shrink-0" />
                                    <span>{bullet}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {version.key_changes?.length > 0 && (
                            <div className={`p-3 rounded-lg ${c.bg}`}>
                              <h5 className="text-xs font-semibold text-zinc-500 uppercase tracking-wide mb-2">What Changed</h5>
                              <ul className="space-y-1">
                                {version.key_changes.map((change, i) => (
                                  <li key={i} className={`text-xs ${c.accent}`}>• {change}</li>
                                ))}
                              </ul>
                            </div>
                          )}
                          <div className="flex gap-2">
                          <Button
                            onClick={() => handleSaveOptimizedCV(optimizeDialog.result.id, version.id, version.label)}
                            disabled={optimizeDialog.saving === version.id}
                            className="flex-1 bg-zinc-900 hover:bg-zinc-800 text-white rounded-xl"
                            data-testid={`save-optimized-cv-btn-${idx}`}
                          >
                            {optimizeDialog.saving === version.id ? (
                              <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Saving...</>
                            ) : (
                              <><Save className="w-4 h-4 mr-2" /> Save to My CVs</>
                            )}
                          </Button>
                          <Button
                            onClick={() => handleDownloadOptimizedPDF(optimizeDialog.result.id, version.id, version.label)}
                            variant="outline"
                            className="rounded-xl border-zinc-300"
                            data-testid={`download-optimized-pdf-btn-${idx}`}
                          >
                            <Download className="w-4 h-4 mr-2" /> PDF
                          </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
                <Button
                  onClick={() => { setOptimizeDialog({ open: false, job: null, result: null, saving: null }); setExpandedVersion(null); setCompareVersions([]); setShowCompare(false); }}
                  variant="outline"
                  className="w-full rounded-xl mt-2"
                  data-testid="optimize-close-btn"
                >
                  Close
                </Button>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default JobFeed;
